import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../common/Toast';
import useAnnouncement from '../../hooks/useAnnouncement';
import { getCurrentSeasonalEffect } from '../seasonal/seasonalConfig';
import SeasonalEffects from '../seasonal/SeasonalEffects';
import './Marquee.scss';

/**
 * 單行跑馬燈：顯示文字 + inline 編輯
 */
const MarqueeLine = ({ text, type, canEdit, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const handleClick = () => {
    if (!canEdit) return;
    setEditValue(text || '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    const newText = editValue.trim();
    if (newText === (text || '')) {
      setIsEditing(false);
      return;
    }
    const result = await onSave(type, newText);
    if (result?.error) return;
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="marquee-line editing">
        <input
          type="text"
          className="marquee-input"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleCancel}
          autoFocus
          placeholder={type === 'company' ? '輸入公司公告...' : '輸入部門公告...'}
        />
      </div>
    );
  }

  return (
    <div
      className={`marquee-line ${canEdit ? 'editable' : ''}`}
      onClick={handleClick}
      title={canEdit ? '點擊編輯公告' : ''}
    >
      <div className="marquee-content">
        <span className="marquee-text">
          {type === 'company' ? '📢 ' : '🏢 '}{text}
        </span>
      </div>
    </div>
  );
};

/**
 * Marquee — 跑馬燈公告元件
 *
 * 支援公司公告 + 部門公告兩行堆疊顯示
 * - 兩個都有 → 上行公司、下行部門（~52px）
 * - 只有一個 → 單行居中（40px）
 * - 都沒有 → 純裝飾條（40px），admin 可點擊新增
 */
const Marquee = () => {
  const { profile } = useAuth();
  const toast = useToast();
  const { companyMarquee, departmentMarquee, loading, updateMarquee } = useAnnouncement();
  const [emptyEditing, setEmptyEditing] = useState(null); // 'company' | 'department' | null
  const [emptyEditValue, setEmptyEditValue] = useState('');

  const handleSave = async (type, text) => {
    const { error } = await updateMarquee(type, text);
    if (error) {
      toast.error('更新失敗：' + error);
      return { error };
    }
    toast.success('公告已更新');
    return {};
  };

  // 空白狀態的編輯處理
  const handleEmptySave = async () => {
    const text = emptyEditValue.trim();
    if (!text) {
      setEmptyEditing(null);
      return;
    }
    const result = await handleSave(emptyEditing, text);
    if (!result?.error) {
      setEmptyEditing(null);
      setEmptyEditValue('');
    }
  };

  const handleEmptyCancel = () => {
    setEmptyEditing(null);
    setEmptyEditValue('');
  };

  const handleEmptyKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEmptySave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEmptyCancel();
    }
  };

  if (loading) {
    return <div className="marquee-container" />;
  }

  const role = profile?.role;
  const canEditCompany = ['super_admin', 'company_admin'].includes(role);
  const canEditDepartment = ['super_admin', 'company_admin', 'department_admin'].includes(role);

  const hasCompany = !!companyMarquee;
  const hasDepartment = !!departmentMarquee;
  const hasAny = hasCompany || hasDepartment;
  const hasBoth = hasCompany && hasDepartment;

  const seasonalEffect = getCurrentSeasonalEffect();

  return (
    <div
      className={[
        'marquee-container',
        seasonalEffect ? `season-${seasonalEffect}` : '',
        !hasAny && !emptyEditing ? 'no-announcement' : '',
        hasBoth ? 'dual-line' : '',
      ].filter(Boolean).join(' ')}
    >
      <SeasonalEffects />

      {hasAny ? (
        <div className="marquee-lines">
          {hasCompany && (
            <MarqueeLine
              text={companyMarquee}
              type="company"
              canEdit={canEditCompany}
              onSave={handleSave}
            />
          )}
          {hasDepartment && (
            <MarqueeLine
              text={departmentMarquee}
              type="department"
              canEdit={canEditDepartment}
              onSave={handleSave}
            />
          )}
        </div>
      ) : emptyEditing ? (
        <div className="marquee-lines">
          <div className="marquee-line editing">
            <input
              type="text"
              className="marquee-input"
              value={emptyEditValue}
              onChange={(e) => setEmptyEditValue(e.target.value)}
              onKeyDown={handleEmptyKeyDown}
              onBlur={handleEmptyCancel}
              autoFocus
              placeholder={emptyEditing === 'company' ? '輸入公司公告...' : '輸入部門公告...'}
            />
          </div>
        </div>
      ) : (
        <div
          className={`marquee-wrapper ${canEditCompany || canEditDepartment ? 'editable' : ''}`}
          onClick={() => {
            if (!canEditCompany && !canEditDepartment) return;
            const defaultType = canEditCompany ? 'company' : 'department';
            setEmptyEditing(defaultType);
            setEmptyEditValue('');
          }}
          title={canEditCompany || canEditDepartment ? '點擊新增公告' : ''}
        >
          <div className="marquee-content">
            <span className="marquee-text" />
          </div>
        </div>
      )}
    </div>
  );
};

export default Marquee;
